import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import AnnouncementBar from '@/pages/home/components/AnnouncementBar';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import type { HomeConfig, ProductSectionItem } from '@/pages/admin/homeDesign/page';

interface DbProduct {
  id: string;
  name: string;
  price: number;
  original_price: number | null;
  category: string | null;
  images: string[] | null;
  stock: number;
  sales: number;
  status: string;
}

const DEFAULT_PAGE_LAYOUT = [
  { id: 'banner', type: 'banner' as const, visible: true },
  { id: 'pointsCard', type: 'pointsCard' as const, visible: true },
  { id: 'benefit', type: 'benefit' as const, visible: true },
  { id: 'announcement', type: 'announcement' as const, visible: true },
  { id: 'category', type: 'category' as const, visible: true },
  { id: 'midBanner', type: 'midBanner' as const, visible: true },
  { id: 'bottomBanner', type: 'bottomBanner' as const, visible: true },
];

const DEFAULT_CONFIG: HomeConfig = {
  themeColor: 'emerald',
  storeName: '',
  logoImage: '',
  logoBgGradient: 'from-emerald-500 to-emerald-400',
  banners: [],
  categories: [
    { icon: 'ri-drop-line', label: '洗发护发', color: 'bg-emerald-50', iconColor: 'text-emerald-600', path: '/shop?category=洗发护发' },
    { icon: 'ri-heart-pulse-line', label: '头皮护理', color: 'bg-rose-50', iconColor: 'text-rose-500', path: '/shop?category=头皮护理' },
    { icon: 'ri-tools-line', label: '护发工具', color: 'bg-amber-50', iconColor: 'text-amber-500', path: '/shop?category=护发工具' },
    { icon: 'ri-gift-2-line', label: '套装礼盒', color: 'bg-violet-50', iconColor: 'text-violet-500', path: '/shop?category=套装礼盒' },
    { icon: 'ri-capsule-line', label: '口服营养', color: 'bg-sky-50', iconColor: 'text-sky-500', path: '/shop?category=口服营养' },
    { icon: 'ri-store-2-line', label: '全部商品', color: 'bg-gray-100', iconColor: 'text-gray-500', path: '/shop' },
  ],
  benefits: [
    { icon: 'ri-leaf-line', title: '纯植物配方', desc: '天然草本精华' },
    { icon: 'ri-test-tube-line', title: '科学研发', desc: '临床验证有效' },
    { icon: 'ri-shield-check-line', title: '安全无害', desc: '无添加无刺激' },
    { icon: 'ri-award-line', title: '品质保证', desc: '正品溯源保障' },
  ],
  midBanner: {
    title: '', subtitle: '', tag: '',
    image: '',
    path: '',
  },
  bottomBanner: {
    title: '邀好友 · 赚积分', subtitle: '每邀一人奖励 500 积分',
    image: '',
    path: '/community',
  },
  pointsCardGradient: 'from-emerald-600 to-emerald-500',
  pageLayout: [],
  productSections: [],
};

// ─── Product Section Component ────────────────────────────────────────────────

const HomeProductSection = ({
  section,
  allProducts,
}: {
  section: ProductSectionItem;
  allProducts: DbProduct[];
}) => {
  const navigate = useNavigate();
  const sectionProducts = section.productIds
    .map((id) => allProducts.find((p) => p.id === id))
    .filter(Boolean) as DbProduct[];

  if (sectionProducts.length === 0) return null;

  return (
    <LazySection animation="fadeUp" delay={0}>
      <div className="bg-white mt-3 py-4">
        <div className="flex items-center justify-between px-4 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-rose-400 rounded-full" />
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{section.title}</h3>
              {section.subtitle && <p className="text-xs text-gray-400">{section.subtitle}</p>}
            </div>
          </div>
          <button onClick={() => navigate('/shop')} className="text-rose-500 text-xs flex items-center gap-0.5 cursor-pointer whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        {section.displayStyle === 'list' ? (
          <div className="px-4 space-y-2">
            {sectionProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => navigate(`/product/${product.id}`)}
                className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 cursor-pointer active:scale-[0.98] transition-transform"
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
                  {product.images?.[0] && (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top" loading="lazy" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug">{product.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{product.category}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-emerald-600 font-bold text-sm">{Number(product.price).toLocaleString()}</span>
                    <span className="text-emerald-600 text-xs">积分</span>
                    {product.original_price && (
                      <span className="text-gray-400 text-xs line-through">{Number(product.original_price).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                  className="bg-rose-500 text-white text-xs px-3 py-1.5 rounded-full flex-shrink-0 cursor-pointer whitespace-nowrap"
                >
                  兑换
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 px-4">
            {sectionProducts.map((product, idx) => (
              <LazySection key={product.id} animation="fadeUp" delay={idx * 60}>
                <div
                  onClick={() => navigate(`/product/${product.id}`)}
                  className="bg-white border border-gray-100 rounded-xl overflow-hidden cursor-pointer active:scale-95 transition-transform"
                >
                  <div className="w-full aspect-square bg-gray-100 overflow-hidden">
                    {product.images?.[0] && (
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top" loading="lazy" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug mb-2">{product.name}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-emerald-600 font-bold text-base">{Number(product.price).toLocaleString()}</span>
                        <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                      </div>
                      {product.original_price && (
                        <span className="text-gray-400 text-xs line-through">{Number(product.original_price).toLocaleString()}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">已兑 {product.sales.toLocaleString()}</p>
                  </div>
                </div>
              </LazySection>
            ))}
          </div>
        )}
      </div>
    </LazySection>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [currentBanner, setCurrentBanner] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dbProducts, setDbProducts] = useState<DbProduct[]>([]);
  const [realPoints, setRealPoints] = useState<number | null>(null);
  const [homeConfig, setHomeConfig] = useState<HomeConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    supabase
      .from('admin_settings')
      .select('value')
      .eq('key', 'home_config')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try {
            const parsed = JSON.parse(data.value) as HomeConfig;
            // 确保 pageLayout 有值，且包含公告栏组件
            let layout = parsed.pageLayout && parsed.pageLayout.length > 0
              ? parsed.pageLayout
              : DEFAULT_PAGE_LAYOUT;
            // 兼容旧配置：如果没有 announcement 组件，插入到 category 前面
            if (!layout.find((c) => c.id === 'announcement')) {
              const catIdx = layout.findIndex((c) => c.id === 'category');
              const insertIdx = catIdx >= 0 ? catIdx : layout.length;
              layout = [
                ...layout.slice(0, insertIdx),
                { id: 'announcement', type: 'announcement' as const, visible: true },
                ...layout.slice(insertIdx),
              ];
            }
            setHomeConfig({
              ...DEFAULT_CONFIG,
              ...parsed,
              pageLayout: layout,
              productSections: parsed.productSections || [],
            });
          } catch {
            setHomeConfig({ ...DEFAULT_CONFIG, pageLayout: DEFAULT_PAGE_LAYOUT });
          }
        } else {
          setHomeConfig({ ...DEFAULT_CONFIG, pageLayout: DEFAULT_PAGE_LAYOUT });
        }
      });
  }, []);

  useEffect(() => {
    if (!user?.id) { setRealPoints(null); return; }
    supabase.from('custom_users').select('points').eq('id', user.id).maybeSingle()
      .then(({ data }) => { if (data != null) setRealPoints(data.points); });
  }, [user]);

  // 购物车数量：从数据库读取，并监听实时变化
  useEffect(() => {
    if (!user?.id) { setCartCount(0); return; }

    const fetchCartCount = () => {
      supabase
        .from('cart_items')
        .select('quantity')
        .eq('user_id', user.id)
        .then(({ data }) => {
          if (data) {
            const total = data.reduce((sum, item) => sum + (item.quantity ?? 1), 0);
            setCartCount(total);
          }
        });
    };

    fetchCartCount();

    // 实时监听购物车变化
    const channel = supabase
      .channel(`home-cart-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cart_items', filter: `user_id=eq.${user.id}` },
        () => { fetchCartCount(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  useEffect(() => {
    supabase.from('products')
      .select('id, name, price, original_price, category, images, stock, sales, status')
      .eq('status', '上架')
      .order('sales', { ascending: false })
      .then(({ data }) => { if (data) setDbProducts(data as DbProduct[]); });
  }, []);

  const activeBanners = homeConfig.banners;

  const nextBanner = useCallback(() => {
    setCurrentBanner((p) => (p + 1) % activeBanners.length);
  }, [activeBanners.length]);

  useEffect(() => { setCurrentBanner(0); }, [activeBanners.length]);
  useEffect(() => {
    const timer = setInterval(nextBanner, 4000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayPoints = realPoints ?? profile?.points ?? 0;
  const hotProducts = dbProducts.slice(0, 4);
  const newProducts = dbProducts.slice(-4).reverse();
  const currentB = activeBanners[currentBanner] ?? activeBanners[0];
  const activeLayout = homeConfig.pageLayout && homeConfig.pageLayout.length > 0
    ? homeConfig.pageLayout
    : DEFAULT_PAGE_LAYOUT;

  // ── Section renderers ──

  const renderBannerBlock = () => (
    <div className="relative -mt-14" style={{ height: '520px' }}>
      <div className="absolute inset-0 overflow-hidden">
        {activeBanners.map((b, i) => (
          <div key={b.id} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === currentBanner ? 1 : 0 }}>
            <img
              src={b.image}
              alt={b.title}
              className="w-full h-full object-cover object-top"
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'low'}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
          </div>
        ))}
      </div>
      {currentB && (
        <div className="absolute bottom-0 left-0 right-0 px-5 pb-8">
          {currentB.tag && <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full mb-3 border border-white/30">{currentB.tag}</span>}
          <h2 className="text-white font-bold text-3xl leading-tight mb-2 whitespace-pre-line" style={{ fontFamily: "'Noto Serif SC', serif" }}>{currentB.title}</h2>
          <p className="text-white/80 text-sm mb-4">{currentB.subtitle}</p>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/product/${currentB.productId}`)} className="bg-white text-gray-900 text-sm font-semibold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap">{currentB.cta}</button>
            <button onClick={() => navigate('/shop')} className="text-white/80 text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap">查看全部 <i className="ri-arrow-right-line" /></button>
          </div>
        </div>
      )}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
        {activeBanners.map((_, i) => (
          <button key={i} onClick={() => setCurrentBanner(i)} className={`rounded-full transition-all duration-300 cursor-pointer ${i === currentBanner ? 'h-4 w-1.5 bg-white' : 'h-1.5 w-1.5 bg-white/50'}`} />
        ))}
      </div>
      <div onClick={() => document.getElementById('home-content')?.scrollIntoView({ behavior: 'smooth' })}
        className={`absolute bottom-28 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 transition-opacity duration-500 cursor-pointer ${isScrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <span className="text-white/70 text-xs tracking-widest">下滑探索</span>
        <div className="flex flex-col items-center" style={{ animation: 'scrollBounce 1.4s ease-in-out infinite' }}>
          <i className="ri-arrow-down-s-line text-white/90 text-2xl -mb-3" />
          <i className="ri-arrow-down-s-line text-white/50 text-2xl" />
        </div>
      </div>
    </div>
  );

  const renderPointsBlock = () => (
    <LazySection animation="fadeUp" delay={0}>
      <div id="home-content" className="px-4 mt-3 relative z-10">
        <div className={`bg-gradient-to-r ${homeConfig.pointsCardGradient} rounded-2xl p-4 cursor-pointer`} onClick={() => navigate('/wallet')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs mb-0.5">我的积分</p>
              <div className="flex items-baseline gap-1">
                <span className="text-white font-bold text-2xl">{user ? displayPoints.toLocaleString() : '—'}</span>
                <span className="text-white/70 text-xs">积分</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={(e) => { e.stopPropagation(); navigate('/shop'); }} className="bg-white/20 text-white text-xs px-4 py-2 rounded-full cursor-pointer whitespace-nowrap font-medium">去兑换</button>
              <button onClick={(e) => { e.stopPropagation(); navigate('/transfer'); }} className="bg-white/20 text-white text-xs px-4 py-2 rounded-full cursor-pointer whitespace-nowrap font-medium">转积分</button>
            </div>
          </div>
        </div>
      </div>
    </LazySection>
  );

  const renderBenefitBlock = () => (
    <LazySection animation="fadeUp" delay={60}>
      <div className="bg-white mt-3 mx-4 rounded-2xl px-4 py-4">
        <div className="grid grid-cols-4 gap-2">
          {homeConfig.benefits.map((b, i) => (
            <div key={`${b.title}-${i}`} className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl">
                <i className={`${b.icon} text-emerald-600 text-lg`} />
              </div>
              <p className="text-xs font-semibold text-gray-800 text-center">{b.title}</p>
              <p className="text-xs text-gray-400 text-center leading-tight">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </LazySection>
  );

  const renderCategoryBlock = () => (
    <LazySection animation="fadeUp" delay={80}>
      <div className="bg-white mt-3 px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 text-sm">商品分类</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {homeConfig.categories.map((cat, i) => (
            <button key={`${cat.label}-${i}`} onClick={() => navigate(cat.path)}
              className={`flex items-center gap-2.5 px-3 py-3 rounded-xl cursor-pointer active:scale-95 transition-all duration-200 ${cat.color}`}>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className={`${cat.icon} text-xl ${cat.iconColor}`} />
              </div>
              <span className="text-xs font-medium text-gray-700">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>
    </LazySection>
  );

  const renderMidBannerBlock = () => (
    <LazySection animation="fadeIn" delay={0}>
      <div className="mt-3 mx-4">
        <div className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform" style={{ height: '160px' }} onClick={() => navigate(homeConfig.midBanner.path)}>
          <img src={homeConfig.midBanner.image} alt={homeConfig.midBanner.title} className="w-full h-full object-cover object-top" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center px-5">
            <span className="text-white/70 text-xs mb-1">{homeConfig.midBanner.tag}</span>
            <p className="text-white font-bold text-xl leading-tight" style={{ fontFamily: "'Noto Serif SC', serif" }}>{homeConfig.midBanner.title}</p>
            <p className="text-white/80 text-xs mt-1 mb-3">{homeConfig.midBanner.subtitle}</p>
            <span className="self-start bg-white text-gray-900 text-xs font-semibold px-4 py-1.5 rounded-full">立即查看</span>
          </div>
        </div>
      </div>
    </LazySection>
  );

  const renderBottomBannerBlock = () => (
    <LazySection animation="fadeUp" delay={0}>
      <div className="mt-3 mx-4 mb-4">
        <div className="relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform" style={{ height: '140px' }} onClick={() => navigate(homeConfig.bottomBanner.path)}>
          <img src={homeConfig.bottomBanner.image} alt={homeConfig.bottomBanner.title} className="w-full h-full object-cover object-top" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center px-5">
            <p className="text-white font-bold text-lg leading-tight" style={{ fontFamily: "'Noto Serif SC', serif" }}>{homeConfig.bottomBanner.title}</p>
            <p className="text-white/80 text-xs mt-1 mb-3">{homeConfig.bottomBanner.subtitle}</p>
            <span className="self-start bg-white text-gray-900 text-xs font-semibold px-4 py-1.5 rounded-full">立即邀请</span>
          </div>
        </div>
      </div>
    </LazySection>
  );

  const renderHotProductsBlock = () => (
    <LazySection animation="fadeUp" delay={0}>
      <div className="bg-white mt-3 py-4">
        <div className="flex items-center justify-between px-4 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-emerald-500 rounded-full" />
            <h3 className="font-bold text-gray-900 text-sm">热门兑换</h3>
          </div>
          <button onClick={() => navigate('/shop')} className="text-emerald-600 text-xs flex items-center gap-0.5 cursor-pointer whitespace-nowrap">查看全部 <i className="ri-arrow-right-s-line" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 px-4">
          {hotProducts.length === 0
            ? [0, 1, 2, 3].map((i) => <div key={i} className="bg-gray-100 rounded-xl aspect-square animate-pulse" />)
            : hotProducts.map((product, idx) => (
              <LazySection key={product.id} animation="fadeUp" delay={idx * 80}>
                <div onClick={() => navigate(`/product/${product.id}`)} className="bg-white border border-gray-100 rounded-xl overflow-hidden cursor-pointer active:scale-95 transition-transform">
                  <div className="w-full aspect-square bg-gray-100 relative overflow-hidden">
                    {product.images?.[0] && <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top" loading="lazy" />}
                    {product.sales > 5000 && <span className="absolute top-2 left-2 bg-rose-500 text-white text-xs px-1.5 py-0.5 rounded-md font-medium">爆款</span>}
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug mb-2">{product.name}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-emerald-600 font-bold text-base">{Number(product.price).toLocaleString()}</span>
                        <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                      </div>
                      {product.original_price && <span className="text-gray-400 text-xs line-through">{Number(product.original_price).toLocaleString()}</span>}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">已兑 {product.sales.toLocaleString()}</p>
                  </div>
                </div>
              </LazySection>
            ))
          }
        </div>
      </div>
    </LazySection>
  );

  const renderNewProductsBlock = () => newProducts.length > 0 ? (
    <LazySection animation="fadeUp" delay={0}>
      <div className="bg-white mt-3 py-4">
        <div className="flex items-center justify-between px-4 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-amber-400 rounded-full" />
            <h3 className="font-bold text-gray-900 text-sm">新品上市</h3>
          </div>
          <button onClick={() => navigate('/shop')} className="text-amber-500 text-xs flex items-center gap-0.5 cursor-pointer whitespace-nowrap">查看全部 <i className="ri-arrow-right-s-line" /></button>
        </div>
        <div className="flex gap-3 px-4 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {newProducts.map((product, idx) => (
            <LazySection key={product.id} animation="slideRight" delay={idx * 80}>
              <div onClick={() => navigate(`/product/${product.id}`)} className="min-w-[150px] bg-white border border-gray-100 rounded-xl overflow-hidden cursor-pointer flex-shrink-0 active:scale-95 transition-transform">
                <div className="w-full h-36 bg-gray-100 overflow-hidden">
                  {product.images?.[0] && <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top" loading="lazy" />}
                </div>
                <div className="p-2.5">
                  <p className="text-xs text-gray-800 font-medium line-clamp-2 leading-snug mb-1.5">{product.name}</p>
                  <div>
                    <span className="text-emerald-600 font-bold text-sm">{Number(product.price).toLocaleString()}</span>
                    <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                  </div>
                </div>
              </div>
            </LazySection>
          ))}
        </div>
      </div>
    </LazySection>
  ) : null;

  const renderLayoutBlock = (type: string, compId: string) => {
    switch (type) {
      case 'banner': return renderBannerBlock();
      case 'pointsCard': return renderPointsBlock();
      case 'benefit': return renderBenefitBlock();
      case 'category': return renderCategoryBlock();
      case 'midBanner': return renderMidBannerBlock();
      case 'bottomBanner': return renderBottomBannerBlock();
      case 'announcement': return <AnnouncementBar />;
      case 'productSection': {
        const section = (homeConfig.productSections || []).find((s) => s.id === compId);
        if (!section) return null;
        return <HomeProductSection section={section} allProducts={dbProducts} />;
      }
      default: return null;
    }
  };

  const hasProductSections = (homeConfig.productSections || []).length > 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">

      {/* Sticky Header */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-transparent'}`}>
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full overflow-hidden flex-shrink-0 ${isScrolled ? 'ring-1 ring-gray-200' : 'ring-2 ring-white/60'} ${!homeConfig.logoImage ? `bg-gradient-to-br ${homeConfig.logoBgGradient}` : ''}`}>
              {homeConfig.logoImage ? (
                <img src={homeConfig.logoImage} alt="logo" className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{homeConfig.storeName?.charAt(0) ?? '我'}</span>
                </div>
              )}
            </div>
            <span className={`text-sm font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: "'Noto Serif SC', serif" }}>
              {homeConfig.storeName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/shop')} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className={`ri-search-line text-xl ${isScrolled ? 'text-gray-600' : 'text-white'}`} />
            </button>
            <button onClick={() => navigate('/cart')} className="w-8 h-8 flex items-center justify-center relative cursor-pointer">
              <i className={`ri-shopping-cart-line text-xl ${isScrolled ? 'text-gray-600' : 'text-white'}`} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      <>
        {activeLayout
          .filter((comp) => comp.visible)
          .map((comp) => (
            <div key={comp.id}>
              {renderLayoutBlock(comp.type, comp.id)}
            </div>
          ))
        }
        {!hasProductSections && (
          <>
            {renderHotProductsBlock()}
            {renderNewProductsBlock()}
          </>
        )}
      </>

    </div>
  );
};

export default HomePage;
